import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@core/auth.service';
import { Observable, from, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import {
  GAME_ONBOARDING_POPUP_DOC,
  GAME_ONBOARDING_POPUP_DOC_CANDIDATES,
  GameOnboardingPopupDoc,
  GameOnboardingPopupId,
  OnboardingPopupCloseReason,
} from 'src/app/interfaces/game-onboarding-popup.interface';
import { Popover } from 'src/app/interfaces/popover.enum';
import {
  ONBOARDING_SPRITE,
  normalizeGameOnboardingPopupId,
} from '../components/onboarding-popup/onboarding-popup.contract';
import { OnboardingPopupComponent } from '../components/onboarding-popup/onboarding-popup.component';

@Injectable({ providedIn: 'root' })
export class GameOnboardingPopupService {
  private readonly firestore = inject(Firestore);
  private readonly matDialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  private readonly docCache = new Map<string, Observable<GameOnboardingPopupDoc | null>>();
  private readonly shownThisSession = new Set<string>();
  private openDialogRef: MatDialogRef<OnboardingPopupComponent> | null = null;
  /** Prevents double-open while Firestore fetch is in flight. */
  private openingPopupId: string | null = null;

  getPopupDoc(popupId: string): Observable<GameOnboardingPopupDoc | null> {
    const normalized = normalizeGameOnboardingPopupId(popupId);
    if (!normalized) {
      return of(null);
    }
    const cached = this.docCache.get(normalized);
    if (cached) {
      return cached;
    }

    // Do NOT gate on isOnline$ — ng-connection-service often reports false on
    // localhost / with adblockers while Firestore still works. That was forcing
    // the static mock. Also never cache null/misses so a later retry can succeed.
    // Prefer FE doc ids, then fall back to legacy CMS ids during rollout.
    const candidates = GAME_ONBOARDING_POPUP_DOC_CANDIDATES[normalized];
    const fetch$ = from(this.fetchFirstExistingPopupDoc(candidates)).pipe(
      map((found) => found?.data ?? null),
      catchError((err) => {
        this.authService.errorHandlerLogs(
          '[GameOnboardingPopupService] Firestore read failed',
          {
            popup_id: normalized,
            candidates,
            code: err?.code,
            message: err?.message,
          }
        );
        return of(null);
      }),
      tap((data) => {
        if (data) {
          this.docCache.set(normalized, of(data).pipe(shareReplay(1)));
        }
      }),
      shareReplay(1)
    );
    return fetch$;
  }

  /** Try preferred then legacy doc ids until one exists. */
  private async fetchFirstExistingPopupDoc(
    candidates: readonly string[]
  ): Promise<{ docId: string; data: GameOnboardingPopupDoc } | null> {
    for (const docId of candidates) {
      const path = `Global_lookup/onboarding/game_new/${docId}`;
      try {
        const snap = await getDoc(doc(this.firestore, path));
        if (snap.exists()) {
          return { docId, data: snap.data() as GameOnboardingPopupDoc };
        }
      } catch (err) {
        this.authService.errorHandlerLogs(
          '[GameOnboardingPopupService] Firestore read failed',
          { path, code: (err as { code?: string })?.code, message: (err as Error)?.message }
        );
      }
    }
    return null;
  }

  /** QA helper: drop in-memory popup doc cache so the next open re-fetches. */
  clearDocCache(): void {
    this.docCache.clear();
  }

  open(
    popupId: string,
    options: {
      force?: boolean;
      /** Open even if another MatDialog is visible (engine pending must not be dropped). */
      allowWithOthers?: boolean;
      /** If Firestore doc missing/inactive, show design mock (engine pending). */
      useMockFallback?: boolean;
    } = {}
  ): Observable<{ reason: OnboardingPopupCloseReason }> {
    const normalized = normalizeGameOnboardingPopupId(popupId);
    if (!normalized) {
      this.authService.errorHandlerLogs(
        '[GameOnboardingPopupService] unknown popup_id',
        { popup_id: popupId }
      );
      return of({ reason: 'unknown' });
    }

    const allowBusy =
      options.force === true || options.allowWithOthers === true;

    if (
      !allowBusy &&
      (this.openingPopupId ||
        this.openDialogRef ||
        this.matDialog.openDialogs.length > 0)
    ) {
      return of({ reason: 'unknown' });
    }

    // Engine pending / force must not be blocked by a prior QA or failed open mark.
    if (
      !options.force &&
      !options.useMockFallback &&
      (this.shownThisSession.has(normalized) || this.hasLocalShown(normalized))
    ) {
      return of({ reason: 'unknown' });
    }

    if (options.force || options.useMockFallback) {
      this.clearShown(normalized);
    }

    this.openingPopupId = normalized;
    const docId = GAME_ONBOARDING_POPUP_DOC[normalized];
    const openingId = normalized;

    return this.getPopupDoc(normalized).pipe(
      take(1),
      switchMap((preloaded) => {
        if (
          !allowBusy &&
          (this.openDialogRef || this.matDialog.openDialogs.length > 0)
        ) {
          return of({ reason: 'unknown' as const });
        }

        // Prefer Firestore content. Mock is only a fallback when force/QA /
        // engine-pending must still show something if the doc is missing/inactive.
        let docToShow: GameOnboardingPopupDoc | null = null;
        let usedMock = false;
        if (preloaded && preloaded.active !== false) {
          docToShow = preloaded;
        } else if (options.force || options.useMockFallback) {
          docToShow = this.buildMockPopupDoc(normalized);
          usedMock = true;
        }

        if (!docToShow) {
          this.authService.errorHandlerLogs(
            '[GameOnboardingPopupService] popup inactive or missing',
            { popup_id: normalized, doc_id: docId }
          );
          return of({ reason: 'inactive' as const });
        }

        if (usedMock) {
          this.authService.errorHandlerLogs(
            '[GameOnboardingPopupService] using mock fallback (Firestore missing/inactive)',
            { popup_id: normalized, doc_id: docId }
          );
        }

        let dialogRef: MatDialogRef<OnboardingPopupComponent>;
        try {
          dialogRef = this.matDialog.open(OnboardingPopupComponent, {
            panelClass: 'game-onboarding-popup-dialog',
            backdropClass: 'game-onboarding-popup-backdrop',
            maxWidth: '340px',
            width: '88vw',
            autoFocus: false,
            restoreFocus: false,
            disableClose: false,
            data: {
              popupId: normalized,
              docId,
              preloaded: docToShow,
            },
          });
        } catch (err) {
          this.authService.errorHandlerLogs(
            '[GameOnboardingPopupService] dialog open failed',
            err
          );
          return of({ reason: 'unknown' as const });
        }

        // Track the newest open ref, but only clear it when *this* dialog closes
        // so allowWithOthers / concurrent opens do not wipe another dialog's state.
        this.openDialogRef = dialogRef;

        if (!options.force) {
          this.markShown(normalized);
        }

        return dialogRef.afterClosed().pipe(
          map((result) => {
            const reason = result?.reason;
            if (
              reason === 'cta' ||
              reason === 'dismiss' ||
              reason === 'inactive' ||
              reason === 'unknown'
            ) {
              return { reason };
            }
            return { reason: 'dismiss' as const };
          }),
          finalize(() => {
            if (this.openDialogRef === dialogRef) {
              this.openDialogRef = null;
            }
          })
        );
      }),
      finalize(() => {
        if (this.openingPopupId === openingId) {
          this.openingPopupId = null;
        }
      })
    );
  }

  private markShown(popupId: string): void {
    this.shownThisSession.add(popupId);
    try {
      localStorage.setItem(this.storageKey(popupId), Popover.isTrue);
    } catch {
      // ignore quota / private mode
    }
  }

  private hasLocalShown(popupId: string): boolean {
    try {
      return localStorage.getItem(this.storageKey(popupId)) === Popover.isTrue;
    } catch {
      return false;
    }
  }

  private storageKey(popupId: string): string {
    return `${Popover.gameOnboardingPopupPrefix}${popupId}`;
  }

  clearShown(popupId?: string): void {
    const ids = popupId
      ? [popupId]
      : (Object.keys(GAME_ONBOARDING_POPUP_DOC) as GameOnboardingPopupId[]);
    ids.forEach((id) => {
      this.shownThisSession.delete(id);
      try {
        localStorage.removeItem(this.storageKey(id));
      } catch {
        // ignore
      }
    });
  }

  /** Build a design-aligned mock when Firestore content is unavailable (local UT). */
  buildMockPopupDoc(popupId: GameOnboardingPopupId): GameOnboardingPopupDoc {
    const typeById: Record<GameOnboardingPopupId, string> = {
      challenge_intro: 'challenge_intro',
      challenge_completed: 'challenge_completed',
      prize_box_opened: 'prize_unlocker_credit_added',
      prize_unlocked: 'prizes_unlocked',
    };
    const headers: Record<GameOnboardingPopupId, string> = {
      challenge_intro: ONBOARDING_SPRITE.header,
      challenge_completed: ONBOARDING_SPRITE.header,
      prize_box_opened: ONBOARDING_SPRITE.headerPrizeBox,
      prize_unlocked: ONBOARDING_SPRITE.headerUnlock,
    };
    const titles: Record<GameOnboardingPopupId, string> = {
      challenge_intro: '',
      challenge_completed: 'FIRST CHALLENGE COMPLETE',
      prize_box_opened: '',
      prize_unlocked: 'YOU UNLOCKED YOUR PRIZES!',
    };
    const prizeLabels: Record<GameOnboardingPopupId, string> = {
      challenge_intro: 'TOP PRIZE',
      challenge_completed: 'PRIZE GIVEN',
      prize_box_opened: 'Added to Prize Unlocker',
      prize_unlocked: 'ADDED TO YOUR ACCOUNT',
    };
    const prizeValues: Record<GameOnboardingPopupId, string> = {
      challenge_intro: '[currency_beautify:100]',
      challenge_completed: 'x25',
      prize_box_opened: '£1',
      prize_unlocked: '£1',
    };
    const line1: Record<GameOnboardingPopupId, string> = {
      challenge_intro: 'Challenge your skill in a variety of ways',
      challenge_completed:
        'You have enough {{ticket_icon}} to open a Prize Box!',
      prize_box_opened:
        'Gain more <i>Prize Box Tokens</i> {{ticket_icon}} to Unlock <b>Bigger Prize boxes</b>, Prizes up to <b>[currency_beautify:100]!</b> {{more_cash_icon}}',
      prize_unlocked:
        'Play Instant-Win games to add prizes to your Prize Unlocker',
    };
    const line2: Record<GameOnboardingPopupId, string> = {
      challenge_intro: 'New challenges given to you <b>Daily!</b>',
      challenge_completed:
        'The more you win the bigger the Prize Boxes you can Unlock. Up to £100!',
      prize_box_opened:
        'To claim <b>Cash</b> {{cash_icon}} and <b>Credit</b> {{credit_icon}} Prizes, Unlock them via the Prize Unlocker',
      prize_unlocked: 'Unlock your Prizes each time you win Cash or Credit',
    };
    const line3: Record<GameOnboardingPopupId, string> = {
      challenge_intro:
        'Remember to exchange Prize Boxes with your Tokens{{ticket_icon}}',
      challenge_completed: '',
      prize_box_opened: '',
      prize_unlocked: '',
    };
    const cta: Record<GameOnboardingPopupId, string> = {
      challenge_intro: 'view',
      challenge_completed: 'SEE MORE',
      prize_box_opened: 'SEE MORE',
      prize_unlocked: 'SEE MORE',
    };
    const row1: Record<GameOnboardingPopupId, string> = {
      challenge_intro: ONBOARDING_SPRITE.instantWin,
      challenge_completed: ONBOARDING_SPRITE.prizeBox,
      prize_box_opened: ONBOARDING_SPRITE.prizeBoxLarge,
      prize_unlocked: ONBOARDING_SPRITE.instantWin,
    };
    const row2: Record<GameOnboardingPopupId, string> = {
      challenge_intro: ONBOARDING_SPRITE.prizeBox,
      challenge_completed: ONBOARDING_SPRITE.prizeBoxLarge,
      prize_box_opened: ONBOARDING_SPRITE.chest,
      prize_unlocked: ONBOARDING_SPRITE.cashAndCredit,
    };
    const row3: Record<GameOnboardingPopupId, string> = {
      challenge_intro: ONBOARDING_SPRITE.cashCredit,
      challenge_completed: '',
      prize_box_opened: '',
      prize_unlocked: '',
    };

    const isIntro = popupId === 'challenge_intro';

    return {
      type: typeById[popupId],
      active: true,
      background: isIntro
        ? 'linear-gradient(180deg, #3B1F7A 0%, #3B1F7A 100%)'
        : popupId === 'prize_box_opened'
          ? 'linear-gradient(180deg, #2B1B5E 0%, #2B1B5E 100%)'
          : '#9B3DFF',
      banner_background:
        isIntro || popupId === 'prize_box_opened'
          ? 'linear-gradient(90deg, #FF9A3C 0%, #FFE55C 100%)'
          : 'linear-gradient(180deg, #FFB800 0%, #FFD84D 100%)',
      banner_text_color:
        isIntro || popupId === 'prize_box_opened' ? '#D40000' : '#E11D2E',
      panel_color: isIntro
        ? '#2B1B5E'
        : popupId === 'prize_box_opened'
          ? '#3D2A7A'
          : '#7A2BC4',
      text_color: '#FFFFFF',
      header_icon_background:
        popupId === 'prize_box_opened' ? '#FFF7C2' : 'transparent',
      cta_background: '#FFFFFF',
      cta_text_color: isIntro ? '#D40000' : '#5B21B6',
      cta_shadow_color: isIntro ? '#FF7A45' : '#7564E3',
      images: {
        header_icon: headers[popupId],
        banner_icon: isIntro
          ? ONBOARDING_SPRITE.prizeBoxLarge
          : popupId === 'prize_box_opened'
            ? ONBOARDING_SPRITE.cashAndCredit
            : ONBOARDING_SPRITE.ticketLarge,
        line_1_icon: row1[popupId],
        line_2_icon: row2[popupId],
        line_3_icon: row3[popupId] || undefined,
        ticket: ONBOARDING_SPRITE.ticket,
        ticket_inline: ONBOARDING_SPRITE.ticket,
        cash_inline: ONBOARDING_SPRITE.cashCredit,
        credit_inline: ONBOARDING_SPRITE.creditLarge,
        more_cash_inline: ONBOARDING_SPRITE.cashAndCredit,
        prize_box: ONBOARDING_SPRITE.prizeBox,
        prize_box_large: ONBOARDING_SPRITE.prizeBoxLarge,
        treasure_chest: ONBOARDING_SPRITE.chest,
        instant_win_games: ONBOARDING_SPRITE.instantWin,
        cash_and_credit: ONBOARDING_SPRITE.cashAndCredit,
      },
      content: {
        en: {
          title: titles[popupId],
          prize_label: prizeLabels[popupId],
          prize_value: prizeValues[popupId],
          line_1: line1[popupId],
          line_2: line2[popupId],
          line_3: line3[popupId] || undefined,
          cta_text: cta[popupId],
        },
      },
      cta: true,
    };
  }
}
